import {
  ConvertRectClipPathToCSSParams,
  ConvertSvgClipPathToCSSParams,
} from './svg-clip-path.type';

/**
 * Convert SVG clip-path to CSS clip-path
 */
export function convertSvgClipPathToCSS({
  clipPathAttr,
  containerElement,
  video,
}: ConvertSvgClipPathToCSSParams): string | null {
  // Extract clip-path ID from url(#svg_21) format
  const match = clipPathAttr.match(/url\(['"]?#([^'")\s]+)['"]?\)/);

  if (!match) return null;

  const clipPathId = match[1];

  // Find the SVG element inside the container
  const svgElement = containerElement.querySelector('svg');

  if (!svgElement) return null;

  // Find the <defs> section inside SVG
  const defs = svgElement.querySelector('defs');

  if (!defs) return null;

  // Find the clipPath element inside <defs>
  const clipPathElement = defs.querySelector(`clipPath#${clipPathId}`);

  if (!clipPathElement) return null;

  // Check if it's a rect-based clip-path (most common case)
  const rect = clipPathElement.querySelector('rect');
  const { x: videoX, y: videoY, width: videoWidth, height: videoHeight } = video;

  if (rect) {
    return convertRectClipPathToCSS({
      rect,
      video: {
        x: videoX,
        y: videoY,
        width: videoWidth,
        height: videoHeight,
      },
    });
  }

  // Check if it's a path-based clip-path
  const path = clipPathElement.querySelector('path');

  if (path) {
    return convertPathClipPathToCSS(path);
  }

  return null;
}

/**
 * Convert rect-based SVG clip-path to CSS inset
 */
function convertRectClipPathToCSS({ rect, video }: ConvertRectClipPathToCSSParams): string {
  // Get clip-path rect coordinates in SVG coordinate system
  const clipX = parseFloat(rect.getAttribute('x') || '0');
  const clipY = parseFloat(rect.getAttribute('y') || '0');
  const clipWidth = parseFloat(rect.getAttribute('width') || '0');
  const clipHeight = parseFloat(rect.getAttribute('height') || '0');

  // Convert to coordinates relative to the video element's bounding box
  // CSS clip-path inset() is relative to the element's border box
  const { height: videoHeight, width: videoWidth, x: videoX, y: videoY } = video;
  const top = clipY - videoY;
  const left = clipX - videoX;
  const bottom = videoHeight - (clipY - videoY + clipHeight);
  const right = videoWidth - (clipX - videoX + clipWidth);

  // Clamp values to ensure they're within bounds
  const clampedTop = Math.max(0, top);
  const clampedLeft = Math.max(0, left);
  const clampedBottom = Math.max(0, bottom);
  const clampedRight = Math.max(0, right);

  return `inset(${clampedTop}px ${clampedRight}px ${clampedBottom}px ${clampedLeft}px)`;
}

/**
 * Convert path-based SVG clip-path to CSS path
 */
function convertPathClipPathToCSS(path: Element): string | null {
  const pathData = path.getAttribute('d');

  if (!pathData) return null;

  return `path('${pathData}')`;
}
